import { createContext } from 'react';
import type { LoginOutcome, LoginResponse, MeResponse } from '../types';

export interface IdentityContextValue {
  me: MeResponse | null;
  loading: boolean;
  /** True only once a `refresh()` call fails with a 401 (a genuine "this session is no longer valid,"
   * not a network error) AFTER this provider had previously established an authenticated `me` — i.e. it
   * distinguishes "your session expired/was revoked" from "you were never signed in" (a cold app load,
   * or an unauthenticated visitor). Cleared back to false by the next successful `refresh()`. See
   * IdentityProvider.tsx's own doc comment on how this is tracked. */
  sessionExpired: boolean;
  /** True only until the very first refresh() (the initial "is there already a session?" check on app
   * load) has settled — never flips true again on a later login()/switchCompany() call. App.tsx's
   * top-level route guard must key its one-time full-page "Loading…" state off this, not off `loading`:
   * `loading` also turns true for the whole duration of a login attempt, and keying the guard off it
   * unmounted (and so silently reset) LoginPage — including any error message it had just set — on every
   * submission, successful or not. */
  initializing: boolean;
  error: string | null;
  /** True after showCompanyPicker() and before the next successful switchCompany() — a purely
   * client-side "let me pick again" state. It does not clear the server-issued company/role claim
   * already on the access token; that only changes once switchCompany() actually succeeds. */
  wantsCompanyPicker: boolean;
  /** True only while `refresh()` is auto-selecting a user's sole active membership (EPN-47 product
   * decision A: a user with exactly one accessible company and no established `activeCompanyId` yet is
   * switched into it automatically, rather than being made to click the only company they can access on
   * `CompanyPickerPage`). App.tsx's route guard must key a brief loading state off this — checked BEFORE
   * the `!me.activeCompanyId` gate — so that window never flashes `CompanyPickerPage`. Goes through the
   * exact same secure `switchCompany()`/`POST /api/me/switch-company` path an explicit switch uses; never
   * retried automatically on failure (see IdentityProvider.tsx's own comment on why). */
  isAutoSelectingCompany: boolean;
  /** Resolves to the full `LoginOutcome` — `'success'` (unchanged existing behavior: `me` is populated
   * via the same `refresh()` this always ran), `'mfaRequired'`, or `'locked'` — so `LoginPage` can branch
   * on it. Still throws/rejects a plain `ApiError` on a 401, exactly as before. */
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginOutcome>;
  /** Completes a login that returned `'mfaRequired'`. On success, populates `me` via the same
   * `refresh()`-driven side effect `login()`'s success case does — the caller should treat it exactly
   * like a normal completed login. Throws `ApiError` (400) with one generic message on any verification
   * failure — see `IdentityGateway.mfaVerify`'s own doc comment. */
  completeMfaVerification: (
    challengeToken: string,
    method: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  switchCompany: (membershipId: string) => Promise<void>;
  /** On success, every refresh token the user holds is revoked server-side (see IdentityGateway's own
   * doc comment) — this also clears local session state, same as logout(), since the caller must be
   * treated as signed out. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  showCompanyPicker: () => void;
  refresh: () => Promise<void>;
}

export const IdentityContext = createContext<IdentityContextValue | null>(null);
